import React, { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router";
import loadable from "@loadable/component";
import { Helmet } from "react-helmet-async";
import Menu from "../../core/components/Menu";
import Loading from "../../core/components/Loading";
import "./AppShell.css";
import TestDataProvider from "../../core/context/TestDataProvider";
import About from "../../core/components/Info";
import Tracking from "../../core/components/Tracking";
import useAnalytics from "../../core/hooks/useAnalytics";
import Logo from "../static/logo57.png";
import answerData from "../data/answers.json";
import mapRuleToAnchor from "../utils/mapRuleToAnchor";
import LanguagePicker from "../../core/components/LanguagePicker";
import Item from "../../core/components/Item";
import us from "../../img/us.svg";
import es from "../../img/es.svg";
import de from "../../img/de.svg";
import fr from "../../img/fr.svg";
import QuizCatalog from "../../core/components/quizzes/QuizCatalog";
import QuizSettings from "../../core/components/quizzes/QuizSettings";
import QuizPDFViewer from "../../core/components/quizzes/QuizPDFViewer";

const HandballRules = loadable(() => import("./HandballRules"), {
  fallback: <Loading />,
});

const RulesTest = loadable(() => import("../../core/components/rules-test/RulesTest"), {
  fallback: <Loading />,
});

const BeachRulesTest = () => (
  <RulesTest mapRuleToAnchor={mapRuleToAnchor} />
);

const Stats = loadable(() => import("../../core/components/stats/Stats"), {
  fallback: <Loading />,
});

const AppShell: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const { updateConfig } = useAnalytics();

  useEffect(() => updateConfig({
    anonymize_ip: true,
    page_path: window.location.pathname,
  }), []);

  return (
    <div id="page-wrapper">
      <Helmet htmlAttributes={{ lang: i18n.language }} />
      <Menu logo={Logo} link="https://usabeachtour.online/" />
      <div id="page-body">
        <Tracking />
        <header>
          <h1>{t("app.title")}</h1>
          <LanguagePicker>
            <Item code="en">
              <img src={us} alt="English" />
              <span>English</span>
            </Item>
            <Item code="es">
              <img src={es} alt="Español" />
              <span>Español</span>
            </Item>
            <Item code="de">
              <img src={de} alt="Deutsch" />
              <span>Deutsch</span>
            </Item>
            <Item code="fr">
              <img src={fr} alt="Français" />
              <span>Français</span>
            </Item>
          </LanguagePicker>
        </header>
        <TestDataProvider answerData={answerData}>
          <Route path="/" element={<BeachRulesTest />} />
          <Route path="/quizzes" element={<QuizCatalog />} />
          <Route path="/quizzes/:quizId" element={<QuizSettings />} />
          <Route path="/quizzes/:quizId/runs/:runId" element={<Stats />} />
          <Route path="/quizzes/:quizId/runs/:runId/pdf" element={<QuizPDFViewer />} />
          <Route path="/rules" element={<HandballRules />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/about" element={<About />} />
          <Route path="/*" element={<BeachRulesTest />} />
        </TestDataProvider>
      </div>
    </div>
  );
};

export default AppShell;
